import type { DateTime } from "luxon"

import type { AlertSettingStatesById } from "./alerts.types"
import type { DeviceGroupRef } from "./group.types"
import type { RecordType } from "./dashboard.types"
import type { Geolocation } from "./geolocation.types"
import type { Powerline } from "./powerlines.types"
import type { Tower } from "./towers.types"
import type { SelectOption, InputRules } from "./common.types"
import type { Span } from "./spans.types"
import type { DeviceFeatures } from "./features.types"

export type DeviceConfig = {
  DII?: number
  motion_sens_freq?: string
  telemetry_url?: string
  t_send?: string
  t_sens?: string
  motion_sens_time?: string
}

export type InstallationType = "powerline" | "tower" | "uninstalled"

/**
 * Where a device sits, at both levels: the asset it is clamped to, and the line
 * that asset belongs to. A line install is on a span, everything else on a
 * tower, and either way the line is the second half of the answer.
 */
export interface InstalledOnInfo {
  type?: "span" | "tower"
  /** Known as soon as the device is; the record behind it is fetched where it
   *  is drawn, so only the rows on screen ask for one. */
  spanId?: number | null
  tower?: Tower
  powerline?: Powerline
}

export type InstalledOnLoading = { towers: boolean; powerlines: boolean }

export type MountingPointOrientation =
  | "facing_front"
  | "facing_right"
  | "facing_left"
  | "facing_back"

export type MountingPoint = {
  orientation?: MountingPointOrientation | null
  height?: number
  face?: string
  section?: { vertical_position?: string; section_type?: string }
  member?: { member_type?: string; horizontal_position?: string }
}

export type DeviceSettings = {
  backend?: string
  clamp_type?: string | null
  device_version?: string
  features?: DeviceFeatures
  geo_location?: Geolocation
  installation_type?: InstallationType
  installed_at?: number | null
  lpc_distance?: number | null
  magnetometer_distance?: number
  mounting_point?: MountingPoint
  orientation_offset?: number[]
  orientation?: string | null
  powerline_id?: number | null
  span_id?: number | null
  tower_id?: number | null
}

export type Device = {
  activated_at?: number
  active?: boolean
  created_at: number
  dashboard_id?: string
  device_configuration?: DeviceConfig
  device_type: string
  enterprise_id?: number
  firmware_version?: string
  hardware_version?: string
  id: number
  last_message_timestamp?: number
  less_id: number
  name: string
  qr_id?: string
  settings?: DeviceSettings
  sold?: boolean
  updated_at: number
}

export interface DeviceWGroups {
  device: Device
  groups: DeviceGroupRef[]
}

export interface IDeviceWRelsById {
  [key: number]: DeviceWGroups
}

export interface DeviceWGroupsNStates extends DeviceWGroups {
  settingsStates: AlertSettingStatesById
}

export interface DeviceWGroupsNStatesNStatus extends DeviceWGroupsNStates {
  status: DeviceStatus
}

export interface DeviceRowInfo extends DeviceWGroupsNStatesNStatus {
  installedOn: InstalledOnInfo
}

export interface DeviceRowsByInstallationStatus {
  installed: DeviceRowInfo[]
  notInstalled: DeviceRowInfo[]
  all: DeviceRowInfo[]
}

export interface IDevsWRelsByIdWStates {
  [key: string]: DeviceWGroupsNStates
}

export interface DevicesByInstallationStatus {
  notInstalled: IDevsWRelsByIdWStates
  installed: IDevsWRelsByIdWStates
  all: IDevsWRelsByIdWStates
}

export interface DevsWRelsByIdWStatesNStatus {
  [key: string]: DeviceWGroupsNStatesNStatus
}

export interface ILessIdByDeviceId {
  [key: number]: number
}

export interface DeviceReq {
  id: number
}

export interface DevicesReq {
  params: {
    org_id: number
  }
}

export type DeviceRes = Device

export type DevicesRes = Device[]

export interface UpdateDeviceNameReq {
  less_id: number
  name: string
}

export interface UpdateDeviceSettingsReq {
  less_id: number
  settings: Partial<DeviceSettings>
}

export interface EditableFeaturesReq {
  lessId: number
}

export interface EditableFeaturesRes {
  emissivity: number | null
}

export interface UpdateEditableFeaturesReq {
  lessId: number
  emissivity: number
}

export interface DevicesTableRow {
  id: number
  alertStatus: { status: boolean; lastDataReceived: DateTime | undefined }
  device: { name: string; id: number }
  deviceNameCsv: string
  deviceIdCsv: number
  deviceVersion: string
  installedOn: InstalledOnColumnContent
  groups: { id: number; name: string }[]
  lastDataReceived: { value: string; statusesLoading: boolean }
  reportingStatus: { value: DeviceReportingStatus; statusesLoading: boolean }
  batteryStatus: {
    isLoading: boolean
    status: string | null
    timestamp: string | null
    value: number | null
  }
}

export interface AddDevicesToGroupTableRow {
  id: number
  name: string
  deviceId: number
  groups: { id: number; name: string }[]
}

export interface CreateDeviceReq {
  orgId: number
  lessId: number
  deviceName: string
  deviceType: string
  settings: {
    backend: string
  }
}
export interface SellDeviceRes {
  qr_id: string
}
export interface SellDeviceReq {
  lessId: number
}
export interface ActivateDeviceReq {
  orgId: number
  lessId: number
  qrId: string
  groupId: number
  deviceName: string
}
export interface DeleteDeviceReq {
  lessId: number
}

export type DeviceReportingStatus =
  | "reporting"
  | "active"
  | "intermittent"
  | "inactive"
  | "unknown"

/** A device as the asset headers list it: enough to name, link and rank it. */
export interface ContextDevice {
  id: number
  name: string | null
  status: DeviceReportingStatus
  hasAlert: boolean
}

export interface DevicesByStatus {
  reporting: RecordType
  active: RecordType
  intermittent: RecordType
  stopped: RecordType
  inactive: RecordType
  uninstalled: RecordType
}

export interface DevicesByAlertStatus {
  triggered: RecordType
  notTriggered: RecordType
}

export type BatteryStatus = "charging" | "discharging"

export type BatteryStatusRef = { value: number; status: BatteryStatus | null | undefined }

export type DeviceStatus = {
  less_id: number
  last_message: string
  battery_level?: number
  battery_status?: BatteryStatus | null
  battery_data_received_at?: string
  next_expected_report?: string
}

export type DeviceStatusesById = {
  [key: number]: DeviceStatus
}

export type InstalledOnColumnContent = {
  installedOn?: InstalledOnInfo
  loading?: InstalledOnLoading
}

export type InstalledOnColumn = {
  data: InstalledOnColumnContent
}

export interface InstallationDetailsForm {
  installedAt: string
  installationType: InstallationType | ""
  // Powerline fields
  orientation: string
  anchoringPointDistance: number | string | ""
  powerlineId: number | string | ""
  spanId: number | string | ""
  phaseId: string
  towerAId: number | string | ""
  towerBId: number | string | ""
  // Tower fields
  towerId: number | string | ""
}

interface CustomInputProps {
  name: string
  placeholder?: string
  initialData?: Powerline | Tower | Span
}

export type CustomInput = React.ComponentType<CustomInputProps>

export function customInputOf<T extends Powerline | Tower | Span>(
  component: React.ComponentType<{ name: string; placeholder?: string; initialData?: T }>,
): CustomInput {
  return component as CustomInput
}

export interface DetailConfig {
  name: keyof InstallationDetailsForm
  labelKey: string
  type?: "number" | "text" | "select"
  placeholder?: string
  labelOptions?: { reference: string }
  selectOptions?: SelectOption[] | null
  rules?: InputRules
  isTranslatable?: boolean
  readOnly?: boolean
  customInput?: CustomInput
}
